import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { FaFacebookF, FaTwitter, FaWhatsapp, FaShareAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const sizeOrder = ["sm", "md", "lg", "xl"];

// Memoized random product card to prevent re-animation
const RandomProductCard = React.memo(({ product, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    exit={{ opacity: 0, y: 20 }}
  >
    <Link
      to={`/product/${product._id}`}
      state={{}}
      className="bg-white p-4 rounded-2xl shadow-sm hover:shadow-md transition flex flex-col"
    >
      <div className="flex justify-center items-center h-40 bg-gray-50 rounded-xl">
        <img src={product.image} className="max-h-36 object-contain" />
      </div>
      <h3 className="mt-3 text-sm font-medium text-gray-900 capitalize">
        {product.name}
      </h3>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm font-semibold">
          Rs {product.finalPrice.toLocaleString("en-PK")}
        </span>
        {product.discount > 0 && (
          <span className="text-xs text-red-600 bg-red-100 py-1 px-2 rounded">
            {product.discount}%
          </span>
        )}
      </div>
    </Link>
  </motion.div>
));

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedSize: initialSize } = location.state || {};

  const [product, setProduct] = useState(null);
  const [randomProducts, setRandomProducts] = useState([]);
  const [size, setSize] = useState(initialSize || null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  const getBasePrice = (p, selectedSize) =>
    p.sizes && selectedSize && p.sizes[selectedSize] !== undefined
      ? p.sizes[selectedSize]
      : p.price;

  const getDiscountedPrice = (p, selectedSize) => {
    const base = getBasePrice(p, selectedSize);
    return Math.round(base - (base * (p.discount || 0)) / 100);
  };

  const addToCart = () => {
    if (!product) return;
    const unitPrice = getDiscountedPrice(product, size);
    const cartItem = {
      _id: product._id,
      name: product.name,
      image: product.image,
      size,
      quantity,
      unitPrice,
      totalPrice: unitPrice * quantity,
    };
    const existingCart = JSON.parse(localStorage.getItem("cart")) || [];
    const existingIndex = existingCart.findIndex(
      (item) => item._id === cartItem._id && item.size === size,
    );
    if (existingIndex !== -1) existingCart[existingIndex].quantity += quantity;
    else existingCart.push(cartItem);
    localStorage.setItem("cart", JSON.stringify(existingCart));
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success(`${product.name} added to cart`);
  };

  const handleBuyNow = () => {
    if (!product) return;
    const unitPrice = getDiscountedPrice(product, size);
    const item = {
      _id: product._id,
      name: product.name,
      image: product.image,
      size,
      quantity,
      unitPrice,
      totalPrice: unitPrice * quantity,
    };
    sessionStorage.setItem("buyNowItem", JSON.stringify(item));
    navigate("/checkout", { state: { buyNowItem: item } });
  };

  useEffect(() => {
    setQuantity(1);
    setLoading(true);

    const fetchProduct = async () => {
      try {
        const res = await fetch(
          `https://supizza-backend.onrender.com/api/products/${id}`,
        );
        if (!res.ok) {
          setProduct(null);
          return;
        }
        const data = await res.json();
        if (!data || !data._id) {
          setProduct(null);
          return;
        }
        setProduct(data);

        // ===== default size logic =====
        if (data.sizes && Object.keys(data.sizes).length > 0) {
          const available = Object.keys(data.sizes);
          if (initialSize && available.includes(initialSize))
            setSize(initialSize);
          else if (available.includes("md")) setSize("md");
          else {
            const fallback = sizeOrder.find((s) => available.includes(s));
            setSize(fallback || available[0]);
          }
        }
      } catch (err) {
        console.error(err);
        setProduct(null);
      }
    };

    const fetchRandomProducts = async () => {
      try {
        const res = await fetch(
          `https://supizza-backend.onrender.com/api/products`,
        );
        const data = await res.json();
        const others = data.filter((p) => p._id !== id);
        setRandomProducts(
          others
            .sort(() => 0.5 - Math.random())
            .slice(0, 4)
            .map((p) => {
              const firstSize = p.sizes ? Object.keys(p.sizes)[0] : null;
              return {
                ...p,
                firstSize,
                basePrice: getBasePrice(p, firstSize),
                finalPrice: getDiscountedPrice(p, firstSize),
              };
            }),
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    fetchRandomProducts();
  }, [id]);

  // ===== SKELETON LOADER =====
  if (loading) {
    return (
      <div className="min-h-screen px-6 lg:px-16 pt-28 lg:pt-32 pb-16 bg-gray-50 space-y-12 animate-pulse">
        {/* MAIN PRODUCT SKELETON */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 bg-white p-6 rounded-2xl shadow-md">
          <div className="flex justify-center items-center bg-gray-100 p-4 rounded-2xl h-96"></div>
          <div className="flex flex-col gap-3">
            <div className="h-6 w-3/4 bg-gray-200 rounded"></div>
            <div className="h-[2px] w-20 bg-gray-300 rounded mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-6 w-20 bg-gray-200 rounded mt-2"></div>
            <div className="flex gap-2 flex-wrap mt-3">
              <div className="h-8 w-12 bg-gray-200 rounded-full"></div>
              <div className="h-8 w-12 bg-gray-200 rounded-full"></div>
              <div className="h-8 w-12 bg-gray-200 rounded-full"></div>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="h-8 w-8 bg-gray-200 rounded"></div>
              <div className="h-8 w-8 bg-gray-200 rounded"></div>
              <div className="h-8 w-8 bg-gray-200 rounded"></div>
              <div className="h-8 w-8 bg-gray-200 rounded"></div>
            </div>
            <div className="flex gap-2 mt-4">
              <div className="h-10 bg-gray-200 rounded-lg flex-1"></div>
              <div className="h-10 bg-gray-200 rounded-lg flex-1"></div>
            </div>
          </div>
        </div>

        {/* RANDOM PRODUCTS SKELETON */}
        <div className="max-w-6xl mx-auto">
          <h2 className="text-gray-900 font-semibold text-lg mb-6">
            You may also like
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className="bg-white p-4 rounded-2xl shadow-sm flex flex-col animate-pulse"
              >
                <div className="flex justify-center items-center h-40 bg-gray-100 rounded-xl mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="flex items-center justify-between">
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ===== PRODUCT NOT FOUND =====
  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-6">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
          Product Not Found
        </h1>
        <p className="text-gray-500 text-center mb-8 max-w-md">
          Oops! The product you’re looking for doesn’t exist or may have been
          removed.
        </p>
        <button
          onClick={() => navigate("/menu")}
          className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition"
        >
          Go to Menu
        </button>
      </div>
    );
  }

  // ===== ACTUAL PRODUCT =====
  return (
    <div className="min-h-screen px-6 lg:px-16 pt-28 lg:pt-32 pb-16 bg-gray-50">
      <Toaster position="bottom-right" />

      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 bg-white p-6 rounded-2xl shadow-md">
        {/* PRODUCT IMAGE */}
        <div className="flex justify-center items-center bg-gray-50 p-4 rounded-2xl">
          <motion.img
            src={product.image}
            alt={product.name}
            className="max-h-[400px] object-contain rounded-xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* PRODUCT INFO */}
        <div className="flex flex-col gap-3">
          <h1 className="text-gray-900 font-semibold text-xl capitalize">
            {product.name}
          </h1>

          <div className="h-[2px] w-20 bg-red-600 rounded mb-3"></div>

          <p className="text-gray-600 text-sm">{product.description}</p>

          {/* PRICE */}
          <div className="flex items-center gap-4 mt-3">
            <span className="font-semibold text-gray-900 text-lg">
              Rs{" "}
              {(getDiscountedPrice(product, size) * quantity).toLocaleString(
                "en-PK",
              )}
            </span>

            {product.discount > 0 && (
              <>
                <span className="text-gray-400 line-through">
                  Rs{" "}
                  {(getBasePrice(product, size) * quantity).toLocaleString(
                    "en-PK",
                  )}
                </span>
                <div className="bg-red-100 text-red-700 px-2 py-1 text-xs rounded">
                  {product.discount}% OFF
                </div>
              </>
            )}
          </div>

          {/* SIZE SELECTOR */}
          {product.sizes && (
            <div className="flex gap-2 flex-wrap mt-3">
              {Object.keys(product.sizes)
                .sort((a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b))
                .map((s) => (
                  <motion.button
                    key={s}
                    onClick={() => setSize(s)}
                    whileTap={{ scale: 0.9 }}
                    whileHover={{ scale: 1.05 }}
                    animate={size === s ? { scale: 1.08 } : { scale: 1 }}
                    className={`px-3 py-1 text-xs rounded-full border uppercase transition ${
                      size === s
                        ? "bg-red-600 text-white border-red-600"
                        : "border-gray-300 text-gray-600"
                    }`}
                  >
                    {s}
                  </motion.button>
                ))}
            </div>
          )}

          {/* QUANTITY */}
          <div className="flex items-center gap-3 mt-4">
            <span className="text-gray-700 text-sm">Qty:</span>

            <div className="flex items-center border rounded-lg overflow-hidden">
              <motion.button
                whileTap={{ scale: 0.8 }}
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="px-3 py-1 bg-gray-200"
              >
                -
              </motion.button>

              <span className="px-4 py-1">{quantity}</span>

              <motion.button
                whileTap={{ scale: 0.8 }}
                onClick={() => setQuantity(quantity + 1)}
                className="px-3 py-1 bg-gray-200"
              >
                +
              </motion.button>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex gap-2 mt-4">
            <motion.button
              onClick={addToCart}
              className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700"
            >
              Add to Cart
            </motion.button>

            <motion.button
              onClick={handleBuyNow}
              className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-100"
            >
              Buy Now
            </motion.button>
          </div>

          {/* SHARE */}
          <div className="flex items-center gap-3 mt-4">
            <span className="text-gray-500 text-sm">Share:</span>
            <FaFacebookF className="cursor-pointer text-gray-500 hover:text-red-700" />
            <FaTwitter className="cursor-pointer text-gray-500 hover:text-red-700" />
            <FaWhatsapp className="cursor-pointer text-gray-500 hover:text-red-700" />
            <FaShareAlt className="cursor-pointer text-gray-500 hover:text-red-700" />
          </div>
        </div>
      </div>

      {/* RANDOM PRODUCTS */}
      <div className="max-w-6xl mx-auto mt-12">
        <h2 className="text-gray-900 font-semibold text-lg mb-6">
          You may also like
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <AnimatePresence>
            {randomProducts.map((p, i) => (
              <RandomProductCard key={p._id} product={p} index={i} />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
